import { evaluateClientQuota } from '@frontend/api-client';
import { append, isArray, remove } from '@frontend/duck-tape';
import { useState } from '@frontend/react';
import type { MultiSelectAnswer } from '@frontend/react-domain/hooks/useGlobalTaskCreation/helpers';
import {
  Button,
  CheckboxInput,
  Divider,
  LinkButton,
  Text,
  TextInput,
  XStack,
  YStack,
  useShortcut,
} from '@frontend/web-react';
import { applyClass, concatClasses, safeCss } from '@frontend/web-utils';
import { useAnalytics, useRetrieveClientStatusQuery } from '@frontend/web/hooks';
import { useCreateTaskContext } from '@frontend/web/hooks/context/useCreateTaskContext';
import { TaskPreview } from './helpers/TaskPreview';

// Can be used for TextInput, DateInputs, etc.
export const initialQuestionsInputClassName = safeCss(
  'text-p-sm h-[40px] rounded-md border-borderPrimary focus-within:border-borderPrimary bg-surfaceSecondary',
);

export const InitialQuestions = () => {
  const { track } = useAnalytics();
  const {
    answers,
    createTaskState,
    decrementStep,
    incrementStep,
    initialQuestions,
    isOnLastStep,
    isStreaming,
    isSubmitting,
    onUpdateAnswer,
  } = useCreateTaskContext();
  // assumes this is always the last step so we don't need to store additional state in context to return to last question seen
  const [IQIndex, setIQIndex] = useState(0);
  const canSeeNextQuestion = IQIndex < initialQuestions.length;
  const currentQuestion = initialQuestions[IQIndex];
  const currentAnswer = answers[IQIndex];
  const isCurrentIQStreaming = isStreaming && IQIndex + 1 === answers.length;
  const canSubmit = IQIndex === answers.length - 1 && !isStreaming && isOnLastStep;
  const isOnLastQuestion = IQIndex === answers.length - 1 && !isStreaming;
  const { data: clientStatus } = useRetrieveClientStatusQuery();
  const isDraftTask = clientStatus && evaluateClientQuota(clientStatus).isThrottled && !createTaskState.isRecurrence;

  const onClickChoice = (index: number, choice: string) => () => {
    onUpdateAnswer(index, (prev) => ({
      ...prev,
      answer: (prev.answer as MultiSelectAnswer[]).find((a) => a.id === choice)
        ? remove(prev.answer as MultiSelectAnswer[], (a) => a.id !== choice)
        : append(prev.answer as MultiSelectAnswer[], { id: choice, value: choice === 'Other' ? '' : choice }),
    }));
  };

  const onUpdateOtherFreeResponseChoice = (index: number) => (choiceText: string) => {
    onUpdateAnswer(index, (prev) => ({
      // Update the "Other" choice with the new text
      answer: (prev.answer as MultiSelectAnswer[]).map((a) =>
        a.id === 'Other'
          ? {
              id: 'Other',
              value: choiceText,
            }
          : a,
      ),
      choices: prev.choices,
      question: prev.question,
    }));
  };

  const onUpdateFreeResponseAnswer = (index: number) => (text: string) => {
    onUpdateAnswer(index, (prev) => ({ ...prev, answer: text }));
  };

  const onClickBack = () => {
    if (IQIndex === 0) {
      decrementStep();
      return;
    }

    setIQIndex(IQIndex - 1);
  };

  const onClickNext = () => {
    track({
      data: {
        aiGenerated: true,
        // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
        question: initialQuestions[IQIndex]?.question!,
        taskDescription: createTaskState.taskData.description,
      },
      event: 'viewedTaskCreationQuestion',
      topic: 'tasks',
    });

    if (canSubmit) {
      incrementStep();
      return;
    }

    setIQIndex(IQIndex + 1);
  };

  useShortcut({
    Enter: {
      isDisabled: isCurrentIQStreaming,
      onSubmit: onClickNext,
    },
  });

  return (
    <YStack>
      <XStack alignItems="center" className="p-md" justifyContent="spaceBetween">
        <Text type="p-sm">
          Question {IQIndex + 1}/{isStreaming ? '...' : initialQuestions.length}
        </Text>
        {isStreaming ? null : (
          <LinkButton
            color="tertiary"
            disabled={isStreaming}
            label={`Skip all${isOnLastStep ? ' and submit' : ''}`}
            leftIconName="IconPlayerSkipForward"
            onClick={incrementStep}
            textClassName={safeCss('text-p-sm font-normal')}
          />
        )}
      </XStack>
      <Divider />
      <YStack className="pb-md">
        <YStack className="p-md">
          <TaskPreview />
        </YStack>
        <YStack className="px-md pt-sm pb-md">
          <Text type="h5">{currentQuestion?.question}</Text>
        </YStack>
        {isCurrentIQStreaming ? null : isArray(currentAnswer?.answer) ? (
          <YStack gapY="sm">
            {currentQuestion?.choices?.map((choice) => {
              // If "Other" is selected and it's being rendered
              const showOtherTextInput =
                isArray(currentAnswer.answer) &&
                currentAnswer.answer.find((a) => a.id === choice) &&
                choice === 'Other';
              const value = !!(currentAnswer.answer as MultiSelectAnswer[]).find((a) => a.id === choice);
              return (
                <YStack key={choice}>
                  <CheckboxInput
                    className="p-md"
                    disabled={isCurrentIQStreaming}
                    label={choice}
                    onChange={onClickChoice(IQIndex, choice)}
                    value={value}
                  />
                  {showOtherTextInput ? (
                    <TextInput
                      autoCapitalize="sentences"
                      autoFocus
                      className="px-md"
                      classNames={{
                        input: initialQuestionsInputClassName,
                      }}
                      disabled={isCurrentIQStreaming}
                      onChange={onUpdateOtherFreeResponseChoice(IQIndex)}
                      onSubmit={onClickNext}
                      placeholder="Other..."
                      value={(currentAnswer.answer as MultiSelectAnswer[]).find((a) => a.id === 'Other')?.value || ''}
                    />
                  ) : null}
                </YStack>
              );
            })}
          </YStack>
        ) : (
          <TextInput
            autoCapitalize="sentences"
            autoFocus
            className="px-md py-sm"
            classNames={{
              input: initialQuestionsInputClassName,
            }}
            disabled={isCurrentIQStreaming}
            onChange={onUpdateFreeResponseAnswer(IQIndex)}
            onSubmit={onClickNext}
            placeholder="Add comments about your task"
            value={currentAnswer?.answer as string}
          />
        )}
      </YStack>
      <Divider />
      <XStack className="p-md" justifyContent="spaceBetween">
        <Button
          // eslint-disable-next-line tailwindcss/no-arbitrary-value
          className="bg-trueWhite min-w-[120px]"
          color="secondary"
          label="Back"
          onClick={onClickBack}
          type="subtle"
        />
        <Button
          // eslint-disable-next-line tailwindcss/no-arbitrary-value
          className={concatClasses('min-w-[150px]', applyClass(isOnLastQuestion, 'shadow-one'))}
          color={isOnLastQuestion ? 'accent' : 'primary'}
          isLoading={(isStreaming && !canSeeNextQuestion) || isCurrentIQStreaming || isSubmitting}
          italic={isOnLastQuestion}
          label={isOnLastQuestion ? (isDraftTask ? 'Save Draft' : 'Send to copilot') : 'Next'}
          noBorder={!isOnLastQuestion}
          onClick={onClickNext}
          radius="xl"
          uppercase={isOnLastQuestion}
        />
      </XStack>
    </YStack>
  );
};
